import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/shared/hooks/useAuth';
import { auditApi } from '../api/auditApi';

/**
 * Turns a route path into the label shown in the audit log's Page column.
 * Derived rather than looked up in a registry: a registry would silently fall
 * out of date as routes are added, and a stale label in an audit log is worse
 * than a plain one.
 *
 *   /seo/performance         → "SEO · Performance"
 *   /agency/insurance-payers → "Agency · Insurance Payers"
 *   /                        → "Dashboard"
 *
 * Segments that look like ids (cuids, uuids, long opaque strings) are dropped
 * so the label stays readable; the full path is still stored alongside it.
 */
const ACRONYMS = new Set(['seo', 'gbp', 'gsc', 'ai', 'roi', 'crm', 'ivr', 'faq', 'qr']);

function looksLikeId(segment: string): boolean {
  if (/^c[a-z0-9]{20,}$/i.test(segment)) return true; // cuid
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(segment)) return true; // uuid
  if (/^\d+$/.test(segment)) return true; // numeric id
  return segment.length > 24 && !segment.includes('-');
}

export function labelForPath(pathname: string): string {
  const segments = pathname
    .split('/')
    .filter(Boolean)
    .filter((s) => !looksLikeId(s));

  if (segments.length === 0) return 'Dashboard';

  return segments
    .map((segment) =>
      segment
        .split('-')
        .map((word) =>
          ACRONYMS.has(word.toLowerCase())
            ? word.toUpperCase()
            : word.charAt(0).toUpperCase() + word.slice(1),
        )
        .join(' '),
    )
    .join(' · ');
}

/**
 * Reports every route change to the access audit log.
 *
 * Mounted once, inside the router and the auth provider. Only authenticated
 * users are tracked — there is no identity to attribute an anonymous view to,
 * and the sign-in page itself is already covered by the login events.
 */
export function usePageViewTracker(): void {
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  // React 18 StrictMode double-invokes effects in development, and a re-render
  // that does not change the path should not write a second row either.
  const lastReported = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      // Reset so the first page after a fresh sign-in is always recorded, even
      // if it happens to be the same path the previous session ended on.
      lastReported.current = null;
      return;
    }

    const path = location.pathname;
    if (lastReported.current === path) return;
    lastReported.current = path;

    auditApi.recordPageView(path, labelForPath(path));
  }, [location.pathname, isAuthenticated]);
}
